
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as config from '../config'
import * as history from '../history'
import * as client from '../client'
import * as feed from '../feed'
import * as storage from '../storage'
import * as exposed from '../exposed'
import * as execution from '../execution'
import * as backup from '../backup'
import * as inner from '../inner'
import * as trade from '../trade'
import * as security from '../security'
import * as document from '../document'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {Response} from '../core/Response';
import {FieldModelResponse} from '../core/Response';

/**
 * LoginWithPasswordResponse struct
 */
class LoginWithPasswordResponse extends Response {
  /**
   * Initialize struct
   * @param {!Response=} parent
   * @param {!string=} accessToken
   * @param {!string=} refreshToken
   * @constructor
   */
  constructor (parent = new Response(), accessToken = '', refreshToken = '') {
    super()
    super.copy(parent)
    this.AccessToken = accessToken
    this.RefreshToken = refreshToken
  }

  /**
   * Copy struct (shallow copy)
   * @this {!LoginWithPasswordResponse}
   * @param {!LoginWithPasswordResponse} other Other struct
   * @returns {!LoginWithPasswordResponse} This struct
   */
  copy (other) {
    super.copy(other)
    if (other.AccessToken != null) {
      this.AccessToken = other.AccessToken
    } else {
      this.AccessToken = null
    }
    if (other.RefreshToken != null) {
      this.RefreshToken = other.RefreshToken
    } else {
      this.RefreshToken = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!LoginWithPasswordResponse}
   * @returns {!LoginWithPasswordResponse} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new LoginWithPasswordResponseModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new LoginWithPasswordResponseModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!LoginWithPasswordResponse}
   * @param {!LoginWithPasswordResponse} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof LoginWithPasswordResponse)) {
      throw new TypeError('Instance of LoginWithPasswordResponse is required!')
    }
    // noinspection RedundantIfStatementJS
    if (!super.eq(other)) {
      return false
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!LoginWithPasswordResponse}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    let parent = super.toJSON()
    let current = {
      AccessToken: ((this.AccessToken != null) ? this.AccessToken : null),
      RefreshToken: ((this.RefreshToken != null) ? this.RefreshToken : null)
    }
    return { ...parent, ...current }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return LoginWithPasswordResponse.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!LoginWithPasswordResponse} other Object value
   * @returns {!LoginWithPasswordResponse} Created struct
   */
  static fromObject (other) {
    return new LoginWithPasswordResponse().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!LoginWithPasswordResponse}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return LoginWithPasswordResponse.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!LoginWithPasswordResponse}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 6501
  }
}

export { LoginWithPasswordResponse };

/**
 * Fast Binary Encoding LoginWithPasswordResponse field model
 */
class FieldModelLoginWithPasswordResponse extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this._parent = new FieldModelResponse(buffer, 4 + 4)
    this.AccessToken = new fbe.FieldModelString(buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4)
    this.RefreshToken = new fbe.FieldModelString(buffer, this.AccessToken.fbeOffset + this.AccessToken.fbeSize)
  }

  /**
   * Get the core.Response field model
   * @this {!FieldModelLoginWithPasswordResponse}
   * @returns {!FieldModelResponse} core.Response field model
   */
  get parent () {
    return this._parent
  }

  /**
   * Get the field size
   * @this {!FieldModelLoginWithPasswordResponse}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelLoginWithPasswordResponse}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.parent.fbeBody - 4 - 4 + this.AccessToken.fbeSize + this.RefreshToken.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelLoginWithPasswordResponse}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.parent.fbeExtra + this.AccessToken.fbeExtra + this.RefreshToken.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelLoginWithPasswordResponse}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelLoginWithPasswordResponse.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelLoginWithPasswordResponse}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 6501
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelLoginWithPasswordResponse}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelLoginWithPasswordResponse}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) > fbeStructSize) {
      return true
    }
    if (!this.parent.verifyFields(fbeStructSize)) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.AccessToken.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.AccessToken.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.AccessToken.fbeSize

    if ((fbeCurrentSize + this.RefreshToken.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.RefreshToken.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.RefreshToken.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelLoginWithPasswordResponse}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelLoginWithPasswordResponse}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelLoginWithPasswordResponse}
   * @param {!LoginWithPasswordResponse} fbeValue Default value, defaults is new LoginWithPasswordResponse()
   * @returns {!LoginWithPasswordResponse} LoginWithPasswordResponse value
   */
  get (fbeValue = new LoginWithPasswordResponse()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelLoginWithPasswordResponse}
   * @param {!LoginWithPasswordResponse} fbeValue LoginWithPasswordResponse value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) <= fbeStructSize) {
      this.parent.getFields(fbeValue, fbeStructSize)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.AccessToken.fbeSize) <= fbeStructSize) {
      fbeValue.AccessToken = this.AccessToken.get()
    } else {
      fbeValue.AccessToken = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.AccessToken.fbeSize

    if ((fbeCurrentSize + this.RefreshToken.fbeSize) <= fbeStructSize) {
      fbeValue.RefreshToken = this.RefreshToken.get()
    } else {
      fbeValue.RefreshToken = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.RefreshToken.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelLoginWithPasswordResponse}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelLoginWithPasswordResponse}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelLoginWithPasswordResponse}
   * @param {!LoginWithPasswordResponse} fbeValue LoginWithPasswordResponse value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelLoginWithPasswordResponse}
   * @param {LoginWithPasswordResponse} fbeValue LoginWithPasswordResponse value
   */
  setFields (fbeValue) {
    this.parent.setFields(fbeValue)
    this.AccessToken.set(fbeValue.AccessToken)
    this.RefreshToken.set(fbeValue.RefreshToken)
  }
}

export { FieldModelLoginWithPasswordResponse };

/**
 * Fast Binary Encoding LoginWithPasswordResponse model
 */
class LoginWithPasswordResponseModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelLoginWithPasswordResponse(this.buffer, 4)
  }

  /**
   * Get the LoginWithPasswordResponse model
   * @this {!LoginWithPasswordResponseModel}
   * @returns {!FieldModelLoginWithPasswordResponse} model LoginWithPasswordResponse model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!LoginWithPasswordResponseModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!LoginWithPasswordResponseModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return LoginWithPasswordResponseModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!LoginWithPasswordResponseModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelLoginWithPasswordResponse.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!LoginWithPasswordResponseModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!LoginWithPasswordResponseModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!LoginWithPasswordResponseModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!LoginWithPasswordResponseModel}
   * @param {!LoginWithPasswordResponse} value LoginWithPasswordResponse value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!LoginWithPasswordResponseModel}
   * @param {!LoginWithPasswordResponse} value LoginWithPasswordResponse value, defaults is new LoginWithPasswordResponse()
   * @return {!object} Deserialized LoginWithPasswordResponse value and its size
   */
  deserialize (value = new LoginWithPasswordResponse()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new LoginWithPasswordResponse(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new LoginWithPasswordResponse(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!LoginWithPasswordResponseModel}
   * @param {!number} prev Previous LoginWithPasswordResponse model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { LoginWithPasswordResponseModel };
